/**
 * @fileoverview Full-width per-segment pipe-network editor. Every pipe segment
 * (base, each riser, each floor-line run, each room branch) gets its own length
 * and diameter, with live flow / velocity / pressure-drop preview. Edits are
 * buffered in a draft and committed with "Apply changes"; bulk "apply to all"
 * per group and reset-to-defaults are provided for convenience.
 *
 * @module HydraulicBalancingTesting/components/PipeEditorDrawer
 */

import React, { useState } from 'react';
import { Drawer, Table, Select, InputNumber, Button, Space, Tag, Alert, Modal } from 'antd';
import { BookOutlined, ReloadOutlined } from '@ant-design/icons';

import { PIPE_DIAMETER_OPTIONS, PIPES, createDefaultPipes } from '../constants';
import { pipeSegmentReport } from '../physics';

import styles from '../HydraulicBalancingTesting.module.css';

const floorName = (f) => ['Ground floor', '1st floor', '2nd floor'][f] || `Floor ${f}`;
const riserLabel = (f) => (f === 0 ? `Riser foot → ${floorName(0)}` : `${floorName(f - 1)} → ${floorName(f)}`);
const mm = (d) => `${(d * 1000).toFixed(0)} mm`;

/**
 * @param {Object} props
 * @param {boolean} props.open
 * @param {Function} props.onClose
 * @param {Object} props.engine - simulation engine (reads rooms/params, setParam)
 * @param {boolean} props.locked - read-only (mission scaffold)
 * @param {Function} props.onOpenTheory - opens the pipe theory drawer
 */
export const PipeEditorDrawer = ({ open, onClose, engine, locked, onOpenTheory }) => {
  const { rooms, params } = engine;
  // edits are buffered here and only committed to the simulation with "Apply".
  const [draft, setDraft] = useState(params.pipes);
  const report = pipeSegmentReport(rooms, params.pumpHead, draft);
  const dirty = JSON.stringify(draft) !== JSON.stringify(params.pipes);

  // --- draft setters ------------------------------------------------------
  const setSeg = (path, field, value) =>
    setDraft((prev) => {
      if (path[0] === 'base') return { ...prev, base: { ...prev.base, [field]: value } };
      if (path[0] === 'risers') {
        const f = path[1];
        return { ...prev, risers: prev.risers.map((s, i) => (i === f ? { ...s, [field]: value } : s)) };
      }
      if (path[0] === 'floors') {
        const [, f, s] = path;
        return { ...prev, floors: prev.floors.map((row, fi) => (fi === f ? row.map((seg, si) => (si === s ? { ...seg, [field]: value } : seg)) : row)) };
      }
      if (path[0] === 'branches') {
        const i = path[1];
        return { ...prev, branches: prev.branches.map((s, bi) => (bi === i ? { ...s, [field]: value } : s)) };
      }
      return prev;
    });

  const setGroup = (group, field, value) => {
    if (value == null) return;
    setDraft((prev) => {
      if (group === 'risers') return { ...prev, risers: prev.risers.map((s) => ({ ...s, [field]: value })) };
      if (group === 'floors') return { ...prev, floors: prev.floors.map((row) => row.map((s) => ({ ...s, [field]: value }))) };
      if (group === 'branches') return { ...prev, branches: prev.branches.map((s) => ({ ...s, [field]: value })) };
      return prev;
    });
  };

  const apply = () => { engine.setParam('pipes', draft); onClose(); };
  const revert = () => setDraft(params.pipes);
  const resetDefaults = () => setDraft(createDefaultPipes());

  // confirm-on-close guard when there are unapplied edits
  const handleClose = () => {
    if (dirty && !locked) {
      Modal.confirm({
        title: 'Discard unsaved pipe changes?',
        content: 'Your edits have not been applied to the simulation. Close and discard them?',
        okText: 'Discard',
        okType: 'danger',
        cancelText: 'Keep editing',
        onOk: onClose,
      });
    } else {
      onClose();
    }
  };

  // --- columns ------------------------------------------------------------
  const columns = [
    { title: 'Segment', dataIndex: 'label', key: 'label', width: 230, render: (v) => <span style={{ fontWeight: 500 }}>{v}</span> },
    {
      title: 'Length',
      key: 'len',
      width: 132,
      render: (_, r) => (
        <InputNumber size="small" min={0.5} max={30} step={0.5} addonAfter="m" disabled={locked}
          value={r.seg.length} onChange={(v) => v != null && setSeg(r.path, 'length', v)} style={{ width: 116 }} />
      ),
    },
    {
      title: 'Diameter',
      key: 'dia',
      width: 124,
      render: (_, r) => (
        <Select size="small" disabled={locked} value={r.seg.diameter} options={PIPE_DIAMETER_OPTIONS}
          onChange={(v) => setSeg(r.path, 'diameter', v)} style={{ width: 108 }} />
      ),
    },
    { title: 'Flow', key: 'flow', width: 110, align: 'right', render: (_, r) => `${r.m.flowLph.toFixed(0)} l/h` },
    {
      title: 'Velocity',
      key: 'vel',
      width: 120,
      align: 'right',
      render: (_, r) => (
        <span style={r.m.velocity > PIPES.NOISE_VELOCITY ? { color: '#ff4d4f', fontWeight: 600 } : undefined}>
          {r.m.velocity.toFixed(2)} m/s
        </span>
      ),
    },
    { title: 'Δp', key: 'dp', width: 110, align: 'right', render: (_, r) => `${r.m.dpKpa.toFixed(2)} kPa` },
  ];

  // --- rows ---------------------------------------------------------------
  const baseRows = [{ key: 'base', label: 'Heat pump → riser foot', path: ['base'], seg: draft.base, m: report.base }];
  const riserRows = draft.risers.map((seg, f) => ({ key: `r${f}`, label: riserLabel(f), path: ['risers', f], seg, m: report.risers[f] }));
  const floorRows = [];
  draft.floors.forEach((rowArr, f) =>
    rowArr.forEach((seg, s) =>
      floorRows.push({ key: `f${f}-${s}`, label: `${floorName(f)}: column ${s + 1} → ${s + 2}`, path: ['floors', f, s], seg, m: report.floors[f][s] })
    )
  );
  const branchRows = draft.branches.map((seg, idx) => ({
    key: `b${idx}`,
    label: `R${idx + 1} · ${rooms[idx].name}`,
    path: ['branches', idx],
    seg,
    m: report.branches[idx],
  }));

  const Group = ({ title, rows }) => (
    <div className={styles.pipeGroup}>
      <div className={styles.controlSectionTitle}>{title}</div>
      <Table size="small" columns={columns} dataSource={rows} pagination={false} scroll={{ x: 760 }} />
    </div>
  );

  const BulkRow = ({ label, group }) => (
    <div className={styles.pipeQuickRow}>
      <span className={styles.pipeQuickLabel}>{label}</span>
      <Select size="small" disabled={locked} placeholder="Set diameter for all" options={PIPE_DIAMETER_OPTIONS}
        onChange={(v) => setGroup(group, 'diameter', v)} style={{ width: '100%' }} value={null} />
      <InputNumber size="small" disabled={locked} placeholder="Set length for all" min={0.5} max={30} step={0.5} addonAfter="m"
        onChange={(v) => setGroup(group, 'length', v)} style={{ width: '100%' }} value={null} />
    </div>
  );

  return (
    <Drawer
      title="Pipe network editor — per-segment length & diameter"
      placement="right"
      width="100%"
      open={open}
      onClose={handleClose}
      destroyOnClose
      extra={
        <Space>
          <Button icon={<BookOutlined />} onClick={onOpenTheory}>Theory (D⁵)</Button>
          <Button icon={<ReloadOutlined />} onClick={resetDefaults} disabled={locked}>Reset to defaults</Button>
        </Space>
      }
      footer={
        <div className={styles.pipeEditorFooter}>
          {locked ? (
            <Tag>Read-only in this mission</Tag>
          ) : dirty ? (
            <Tag color="warning">Unsaved changes — preview shown in the table</Tag>
          ) : (
            <Tag color="success">All changes applied</Tag>
          )}
          <Space>
            <Button onClick={revert} disabled={!dirty}>Revert</Button>
            <Button type="primary" onClick={apply} disabled={!dirty || locked}>Apply changes</Button>
          </Space>
        </div>
      }
    >
      {/* info banner — top of everything */}
      <Alert
        type="info"
        showIcon
        style={{ marginBottom: 16 }}
        message="Diameter is the strongest lever — pressure drop scales as Δp ∝ L / D⁵"
        description="One pipe size up can cut a segment's loss several-fold. Edit any segment below; the Flow / Velocity / Δp columns preview the result live. Velocity turns red above the ~0.45 m/s noise limit. Changes only affect the simulation once you press Apply."
      />

      <div className={styles.pipeEditorLayout}>
        {/* context + bulk */}
        <div className={styles.pipeEditorSidebar}>
          <PipeSchematic />

          <div className={styles.controlSectionTitle} style={{ marginTop: 14 }}>Apply to all (quick set)</div>
          <BulkRow label="Risers" group="risers" />
          <BulkRow label="Floor lines" group="floors" />
          <BulkRow label="Branches" group="branches" />
          <div className={styles.pipeDefaultsNote}>
            Defaults — riser {mm(PIPES.RISER_DIAMETER)}, floor {mm(PIPES.FLOOR_DIAMETER)}, branch {mm(PIPES.BRANCH_DIAMETER)}.
          </div>
        </div>

        {/* the segment schedule */}
        <div className={styles.pipeEditorTables}>
          <Group title="Base circuit" rows={baseRows} />
          <Group title="Risers" rows={riserRows} />
          <Group title="Floor distribution lines" rows={floorRows} />
          <Group title="Branches (per room)" rows={branchRows} />
        </div>
      </div>
    </Drawer>
  );
};

// ----------------------------------------------------------------------------
// Small schematic for engineering context
// ----------------------------------------------------------------------------

const PipeSchematic = () => {
  const W = 240;
  const H = 180;
  const riserX = 40;
  const floorY = (f) => 150 - f * 48;
  const colX = [90, 150, 210];
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} role="img" aria-label="Network schematic">
      <circle cx={riserX} cy={165} r={6} fill="#fff" stroke="#0050b3" strokeWidth={2} />
      <text x={riserX + 10} y={169} fontSize={9} fill="#888">pump</text>
      <line x1={riserX} y1={165} x2={riserX} y2={floorY(2)} stroke="#0050b3" strokeWidth={3} />
      <text x={riserX - 34} y={floorY(1)} fontSize={9} fill="#0050b3" transform={`rotate(-90 ${riserX - 34} ${floorY(1)})`}>riser</text>
      {[0, 1, 2].map((f) => (
        <g key={f}>
          <line x1={riserX} y1={floorY(f)} x2={colX[2]} y2={floorY(f)} stroke="#1890ff" strokeWidth={2} />
          <text x={riserX} y={floorY(f) - 5} fontSize={8} fill="#888">{floorName(f)}</text>
          {colX.map((cx, c) => (
            <g key={c}>
              <line x1={cx} y1={floorY(f)} x2={cx} y2={floorY(f) - 14} stroke="#e2574c" strokeWidth={2} />
              <rect x={cx - 5} y={floorY(f) - 22} width={10} height={8} rx={1} fill="#e2574c" opacity={0.85} />
            </g>
          ))}
        </g>
      ))}
      <text x={colX[0]} y={H - 4} fontSize={8} fill="#888">red = branches · blue = floor lines</text>
    </svg>
  );
};
